/**
 * Das Bestellboard - `renderBoard` (index.html:6991-7066), die aktive Fassung der Seite.
 *
 * ================= Zur Namensgebung im Bestand =================
 *
 * Der Bestand nennt dieselbe Flaeche an drei Stellen anders: die Seite heisst `#page-board`, das
 * Wurzelelement bekommt die Klasse `grouped-order-list`, und die Tabelle samt ihrer Helfer heisst
 * durchgehend `orderList…`. Eine Funktion `renderOrderList` gibt es NICHT (null Vorkommen in
 * `index.html`) - der Einstiegspunkt ist `renderBoard`, 56 Vorkommen.
 *
 * ================= Der Datenweg, in der Reihenfolge des Originals =================
 *
 * 1. `activeOrderListColumns` -> die sichtbaren Spalten, nach `position` sortiert.
 * 2. Archivfilter (`active` / `all` / `archived`), Volltextsuche, dann der Laenderfilter (NEUBAU
 *    ohne Bestandsvorbild, EU/China-Chips der Werkzeugleiste) -> `facetOrders`. DIESE Liste speist
 *    die Facettenlisten der Spaltenfilter (siehe `Spaltenkopf`, Eigenheit 1) - eine per Chip
 *    ausgeblendete Region faellt deshalb auch aus der Facettenliste der Spalte „Lieferant".
 * 3. Spaltenfilter -> `nachSpaltenfiltern` (daraus zaehlt der Schalter „Neu (n)").
 * 3a. Neu-Filter (NEUBAU, nur Bestellungen mit `hatNeueArtikel`, wenn der Schalter an ist) -> `orders`.
 * 4. Sortierung nach `orderListSortValue`.
 * 5. Gruppierung nach `boardGroupId`, in der Reihenfolge von `BOARD_GROUPS`.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Der Suchheuhaufen (index.html:7003; seit dem KI-Zugang, Stufe 1, in `lib/boardSuche.ts#
 *    bestellungTrifftSuche` - wo hier „unten" steht, ist diese Funktion gemeint) liest IM BESTAND
 *    ZEHN Felder: `orderNo`, `name`,
 *    `trackingNo`, `supplier`, `invoiceNo`, `piNo`, `paymentTerms`, ALLE Werte aus `customFields`
 *    sowie Name und SKU jeder Position. Nicht dabei: Status, Datumsangaben, Betraege, Aufgaben,
 *    Dokumente.
 *
 *    HIER SIND ES SEIT DEM 2026-08-25 NEUN - `trackingNo` ist gestrichen. Das ist die einzige
 *    bewusste Abweichung dieser Liste vom Bestand, und fuer echte Daten aendert sie NICHTS: das
 *    Feld wird im neuen Modell nie mehr befuellt. Nachgemessen an vier Schichten:
 *      - Die Spalte gibt es nicht (`backend/migrations/001_fundament.sql:75-76`: „die fruehere
 *        Einzelspalte tracking_no wird durch die Verknuepfung order_shipments ersetzt"). Auch
 *        `order_extras` traegt sie nicht.
 *      - `GET /api/orders` waehlt sie folglich nicht (`backend/src/domaene/orders.ts:176-190`,
 *        `SELECT o.*, row_to_json(oe) AS extras` - damals kein JOIN auf `order_shipments`/
 *        `shipments`; seit dem Auftrag „Spalte Trackingnummer im Bestellboard" (2026-09-04, siehe
 *        unten) traegt dieselbe Abfrage eine SCALAR-Subquery auf beide Tabellen).
 *      - Der JTL-Merge, der das Board tatsaechlich speist (`state/Speicher.tsx:159`,
 *        `api/jtlBestellungen.ts`), setzt weder `trackingNo` noch `shipmentId`.
 *      - Es gibt keinen Schreibweg: `trackingNo` steht in `KEIN_FELDGRUPPEN_WEG`
 *        (`api/bestellungen.ts:580-583`).
 *    Ein Eintrag, der immer `undefined` ist, taeuscht eine Suchbarkeit vor, die es nicht gibt -
 *    wer eine Trackingnummer eintippte, bekam bis hierher ohne jeden Hinweis null Treffer.
 *
 *    SEIT DEM 2026-09-04 SIND ES ZWOELF (Asana "Suchfunktion erweitern", 1218171668442051): drei
 *    Felder ergaenzt - `bezugsAuftragsNummer` (Bezugsauftrag) und `internerKommentar` (Notizen),
 *    beide schon vorher unveraendert am `Order`-Objekt vorhanden (JTL-Merge), nur bisher nicht im
 *    Heuhaufen; sowie `dsvSendungsnummern` (Sendungsnummer), gespreadet statt als Einzelwert, damit
 *    jede Nummer fuer sich matcht - siehe Nachtrag weiter unten.
 *
 *    WARUM NICHT UEBER DIE SENDUNG ERSETZT - die Nummer liegt heute an `shipments.dsv_shipment_no`,
 *    verknuepft ueber `order_shipments`. Das waere die nuetzlichere Fassung, ist aber nicht
 *    baubar, und zwar aus dem denkbar klarsten Grund: DIESE SEITE HAT KEINE SENDUNGSQUELLE.
 *    Der Sammelabruf beim App-Start, das Speicherfeld `zustand.sendungen`, die hiesige Prop
 *    `sendungen` und die Aufloesung `lib/suche.ts#sendungZuBestellung` sind ERSATZLOS entfallen
 *    (Auftrag „Sendungsabruf beim App-Start entfernen“, 2026-08-22, Commit 24c9349,
 *    Entscheidungsnotiz `docs/superpowers/decisions/2026-08-22-sendungen-app-start.md`).
 *
 *    Die Begruendung dort und die Messung hier vom 2026-08-25 treffen sich in denselben drei
 *    Punkten: der Sammelabruf lieferte in Produktion immer `[]`; die Aufloesung hing an
 *    `order.shipmentId`, das weder `Order` (`lib/types.ts:13`) fuehrt noch das Backend je
 *    ausliefert; und die Antwortform war ohnehin die SERVERFORM `SendungsTreffer` mit
 *    `dsvShipmentNo` (`lib/types.ts:235`), nicht das `trackingNo` der Fixtureform `Sendung`.
 *
 *    Der Eintrag lohnt also erst, wenn die BESTELLLISTE die Verknuepfung selbst mitliefert - dann
 *    als `dsvShipmentNo` der aufgeloesten Sendung, nicht als `trackingNo`. Bis dahin waere jeder
 *    Ersatz ein zweiter Blindgaenger, der nur lebendig aussieht.
 *
 *    GENAU DAS ist am 2026-09-04 eingetreten - in ZWEI getrennten, unabhaengigen Auftraegen, die
 *    `BESTELLUNG_MIT_EXTRAS` (`backend/src/domaene/orders.ts`) je um eine eigene Subquery neben
 *    `dsv_verknuepft` erweitert haben:
 *
 *    - Auftrag "Spalte Trackingnummer im Bestellboard": eine SCALAR-Subquery liefert `dsv_shipment_no`
 *      - bei mehreren verknuepften Sendungen die ZULETZT verknuepfte (`ORDER BY linked_at DESC LIMIT
 *      1`, dasselbe Auswahlkriterium wie in `http/routes/orderShipments.ts`). `oeffentlich()` camelt
 *      sie zu `Order.dsvShipmentNo`, sichtbar als eigene Board-Spalte „Trackingnummer"
 *      (`Bestellzeile.tsx`, `case "dsvShipmentNo"`, ZWEITE Position nach der Bestellnummer,
 *      `config/board.ts#DEFAULT_ORDER_LIST_COLUMNS`). NICHT im Suchheuhaufen - dieser Auftrag fuegt
 *      nur die Spalte hinzu, keine Suchintegration.
 *    - Auftrag "Suchfunktion erweitern" (Asana 1218171668442051): eine zweite, eigene Subquery mit
 *      `array_agg` liefert `dsv_sendungsnummern` - ALLE verknuepften Sendungsnummer(n), nicht nur die
 *      zuletzt verknuepfte. `oeffentlich()` camelt sie zu `Order.dsvSendungsnummern` (Text-Array).
 *      GENAU DIESES Feld - nicht `dsvShipmentNo` - speist seither den Suchheuhaufen unten
 *      (gespreadet, damit jede Nummer einzeln matcht statt als zusammengeklebter String); der
 *      Heuhaufen zaehlt dadurch SEIT DEM 2026-09-04 ZWOELF Felder (siehe unten), nicht neun. Bewusst
 *      NICHT wiederbelebt: `order.shipmentId`, `sendungZuBestellung`, `spurfortschritt`/die DSV-
 *      Fortschrittsanzeige, die volle `Sendung`/`SendungsTreffer`-Form - der obige Befund und die
 *      Ablehnung von "Weg A" in der Entscheidungsnotiz gelten dafuer unveraendert weiter. Auch NICHT
 *      die dort (Abschnitt 3, Punkt 3) skizzierte "Sammelroute fuer Listenansichten" (inkl.
 *      Stationen/Zoll) - die bleibt weiterhin offen.
 *
 *    Beide Felder bestehen nebeneinander, keines ersetzt das andere: `dsvShipmentNo` ist der EINE
 *    aktuelle Wert fuer die Spaltenanzeige, `dsvSendungsnummern` sind ALLE Werte fuer die Suche.
 * 2. Die Sortierung schiebt Leerwerte IMMER ans Ende - unabhaengig von der Richtung (die beiden
 *    ersten `if`-Zweige stehen vor der Richtungsmultiplikation). `av == null` faengt `null` UND
 *    `undefined`; die 0 gilt NICHT als leer.
 * 3. Zahlen werden numerisch verglichen, alles andere ueber `localeCompare(…, 'de', { numeric:
 *    true, sensitivity: 'base' })`. Sind die beiden Werte von verschiedenem Typ (eine Spalte kann
 *    das: `p1amount` liefert eine Zahl oder `''`), faellt der Vergleich auf den Textzweig.
 * 4. Die Gruppierung laeuft ueber die SORTIERTE Liste; innerhalb einer Gruppe bleibt die
 *    Sortierreihenfolge deshalb erhalten.
 * 5. Eine eingeklappte Gruppe zeigt gar keine Zeilen - auch nicht die Leerzeile. Eine
 *    aufgeklappte, leere Gruppe zeigt `<tr class="order-list-group-empty">`.
 * 6. Die Tabellenbreite ist ein Inline-`style` in Pixeln und geht damit in die DOM-Signatur ein.
 * 7. `board.style.gridTemplateColumns = ''` (index.html:6995) raeumt eine Hinterlassenschaft der
 *    Kanban-Ansicht weg. In React gibt es diese Hinterlassenschaft nicht - der Knoten traegt
 *    keinen Inline-Stil, was in der Signatur denselben leeren Wert ergibt.
 *
 * ================= Was hier NICHT ist =================
 *
 * Die beiden Spalteneditoren sind Task 14, der Drawer Task 16 - beide stehen weiterhin als reine
 * Props heraus (kein eigener Zustand hier). Das Kontextmenue (Task 15, `KontextMenue.tsx`) bleibt
 * ebenso extern: `onKontextmenue` reicht Ereignis und Id nur durch, das OB und WO des Menues
 * entscheidet der Aufrufer (Einhaengepunkt, siehe Bericht).
 *
 * Der ZELLENEDITOR (nachgezogen 2026-08-15) ist der zweite Fall dieser Bauart: `orderListEditCell`
 * (index.html:4651) ist im Bestand ein Modul-Global, das genau so lange lebt wie die geladene Seite
 * - hier also Komponentenzustand, wie Suche, Sortierung und Spaltenfilter. Nach aussen geht nur das
 * ERGEBNIS, ueber `onZelleAendern`; das Zeichnen des Bedienelements gehoert `Bestellzeile.tsx`, die
 * Entscheidung "welche Spalte kann ueberhaupt was" `lib/orderListEditor.ts`.
 *
 * Drag&Drop (Task 15, `enableBoardDragAndDrop`, index.html:6952-6988) IST hier verdrahtet: anders
 * als die Editoren/das Menue spielen sich Ziehquelle (`<tr>`) und Ablageziel (`<tbody>`) auf
 * DERSELBEN Seite ab, ein Umweg ueber eine externe Naht wuerde nichts gewinnen. Committet wird nur
 * das ERGEBNIS, ueber `onAendern` - das Schreiben (`save()`) und die Meldung (`toast(...)`) bleiben
 * beim Aufrufer, wie ueberall in diesem Modul.
 *
 * ================= Laenderfilter: seit 2026-08-27 app-weiter Zustand =================
 *
 * Die beiden Chips (bis hierher in der Werkzeugleiste, `Werkzeugleiste.tsx`) sitzen seit dem Auftrag
 * „EU/China-Laenderfilter in die App-Kopfzeile verschieben" in `components/App/Kopfzeile.tsx`, der
 * Zustand in `App.tsx` (dasselbe Muster wie `breiten`/`eingeklappt` dort) statt hier lokal. Diese
 * Seite bekommt ihn seither als Prop `laenderFilter` und wendet die geteilte Regel
 * `lib/laender.ts#istBestellungNachLaenderFilterSichtbar` an: eine Bestellung faellt aus
 * `facetOrders`, wenn ihr aufgeloester Lieferant (`lib/suppliers.ts#orderSupplier`, derselbe Store
 * wie `lieferanten` oben) ueber `lib/laender.ts#supplierRegion` als `"eu"` bzw. `"china"` gilt UND
 * der jeweilige Chip auf `false` steht. Die dritte Region, `"other"` (kein EU-Land, kein China - z. B.
 * der eine US-Lieferant, oder ein Lieferant ohne gepflegtes `iso`), bleibt UNABHAENGIG vom Zustand
 * beider Chips immer sichtbar - es gibt fuer sie keinen Chip, der sie auslesen koennte.
 *
 * `renderBoard` liest KEIN Recht: `hasPermission('manage_settings')` prueft erst
 * `openOrderListEditor` beim Klick (index.html:7411). Dasselbe gilt fuer das Ziehen:
 * `enableBoardDragAndDrop` prueft ebenfalls kein Recht (Bericht Task 15, B1).
 *
 * SEIT DEM 2026-08-15 liest diese Komponente `rechte` allerdings an EINER Stelle - dem Anstoss des
 * Zelleneditors (`zelleAngeklickt`, `edit_orders`). Das ist eine bewusste Abweichung vom Bestand
 * in derselben Bauart wie `spaltenEditorGesperrt` (Auftraggeber-Entscheidung vom 2026-08-11);
 * Begruendung an der Stelle selbst. Die ANZEIGE liest weiterhin kein Recht: gezeichnet wird fuer
 * jeden dasselbe, und auch das Ziehen bleibt ungeprueft, wie im Bestand.
 *
 * ================= Task 3 (Verdrahtung), Befund 5, Option C =================
 *
 * Der `onDrop`-Handler bekommt einen Guard: die gezogene id ueberlebt in `umzugRef` die gesamte
 * Dauer eines Ziehvorgangs (Sekunden, ueber mehrere Neuzeichnungen hinweg) - anders als jede andere
 * Stelle dieser Datei, die IMMER direkt ueber `bestellungen`/`orders` iteriert, ist sie damit die
 * EINZIGE echte "per id nachgeschlagener, ggf. nicht mehr vorhandener Datensatz"-Stelle im gesamten
 * Board. Test: `test/komponenten/Board/umzug.test.tsx`, "Befund 5".
 *
 * ================= Kontotyp "Nur lesen" (Task 6, Neubau) =================
 *
 * Die Prop `nurLeser` (einmal in `App.tsx` aus `state/nurLeser.ts` berechnet; `rechte` hier bleibt davon
 * unberuehrt) schliesst die Schreibwege des Boards, die ohne Rechtepruefung auskamen: das Ziehen einer
 * Zeile zwischen Bereichen (`Bestellzeile.tsx`: nicht ziehbar; der Drop unten tut zusaetzlich nichts), den
 * Sammel-Statuswechsel und das Anlegen einer Bestellgruppe (`Werkzeugleiste.tsx`: kein Knopf). Mehrfachauswahl,
 * Filter und Zellenklick (oeffnet den Drawer) bleiben.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BoardStateSelection } from "../../state/adresse.ts";
import { boardGroupId } from "../../lib/status.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import { normSearch } from "../../lib/format.ts";
import { bestellungTrifftSuche } from "../../lib/boardSuche.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { useEntprelltenAdresswert } from "../../state/useEntprelltenAdresswert.ts";
import {
  activeOrderListColumns,
  orderListColumnWidth,
  orderListSortValue,
  orderListSpaltenfilterAktiv,
  orderListTableWidth,
  orderListZeilePasstFilter,
  orderListZustand,
} from "../../lib/orderList.ts";
import type { OrderListWidths, Spaltenfilter, Spaltenfilterwert } from "../../lib/orderList.ts";
import { orderListEditorRegel } from "../../lib/orderListEditor.ts";
import { istBestellungNachLaenderFilterSichtbar, LAENDERFILTER_VORGABE } from "../../lib/laender.ts";
import {
  istBestellungNachTransportartFilterSichtbar,
  TRANSPORTARTFILTER_VORGABE,
} from "../../lib/transportart.ts";
import type { LaenderFilter, TransportartFilter } from "../../state/arbeitsplatz.ts";
// Zweiter Verbraucher desselben Satzes neben `Suppliers/ExtrasFormular.tsx` - siehe dortige
// Begruendung in `drawerRechte.ts`: EINE Quelle, damit kein Bindestrich zum Halbgeviertstrich wird.
import { hatRecht } from "../Drawer/drawerRechte.ts";
import { zellZiel } from "../../lib/zellZiel.ts";
import type { DrawerZiel } from "../../lib/zellZiel.ts";
import { BOARD_GROUPS } from "../../config/board.ts";
import type { OrderListColumn } from "../../config/board.ts";
import type { Order, ShipmentParentProjektion, Supplier } from "../../lib/types.ts";
import { fmtBetragInWaehrung, fmtSummeJeWaehrung, WAEHRUNG_UNBEKANNT } from "../sammelsendungBetrag.ts";
import { orderSupplier } from "../../lib/suppliers.ts";
import { Sammelsendungszeile } from "./Sammelsendungszeile.tsx";
import { baueAggregatZellen } from "./AggregatZellen.tsx";
import { SammelFaelligkeitZelle } from "./SammelFaelligkeitZelle.tsx";
import { Werkzeugleiste } from "./Werkzeugleiste.tsx";
import { hatNeueArtikel } from "../NeuerArtikelMarke.tsx";
import { BestellgruppeAnlegenDialog } from "../OrderGroups/BestellgruppeAnlegenDialog.tsx";
import { Spaltenkopf } from "./Spaltenkopf.tsx";
import { Gruppenzeile } from "./Gruppenzeile.tsx";
import { Bestellzeile } from "./Bestellzeile.tsx";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { gruppeUeberfahren, gruppeVerlassen, neueZeilenUmzugRef, zeileAbgelegt } from "./ZeilenUmzug.ts";

/**
 * Der Zaehlertext des Boards - im Bestand ein `<span class="order-list-count">` IN der
 * Werkzeugleiste (index.html:7043), seit dem 2026-08-13 die Unterzeile des Seitenkopfs (bewusste
 * Abweichung, siehe `components/App/Seitenkopf.tsx`). Der Wortlaut ist woertlich uebernommen,
 * samt seiner beiden Eigenheiten:
 *
 * 1. Die Grundgesamtheit erscheint NUR bei gesetzten Spaltenfiltern („3 von 12 Bestellungen").
 *    Suchbegriff und Archivfilter zaehlen dafuer NICHT: sie wirken schon auf `facetOrders`, also
 *    auf beide Zahlen.
 * 2. Ein- und Mehrzahl haengen an der GEFILTERTEN Zahl, nicht an der Grundgesamtheit.
 *
 * NEUBAU: der Schalter „Neu" zaehlt wie ein Spaltenfilter mit (Aufrufstelle `+ (neu ? 1 : 0)`), damit
 * „1 von 3 Bestellung" (Einzahl: Eigenheit 2) erscheint und nicht „1 Bestellung" - sonst saehe es aus, als gaebe es nur
 * diese eine. Die Grundgesamtheit bleibt `facetOrders` (vor Spalten- und Neu-Filter).
 */
export function zaehlerText(
  anzahl: number,
  anzahlVorSpaltenfiltern: number,
  aktiveSpaltenfilter: number,
): string {
  return `${anzahl}${aktiveSpaltenfilter ? ` von ${anzahlVorSpaltenfiltern}` : ""} ${
    anzahl === 1 ? "Bestellung" : "Bestellungen"
  }`;
}

/**
 * Eine Sammelsendung in EINEM Board-Bereich: die dort liegenden (sichtbaren) Bestellungen. Dieselbe
 * Sendung kann in mehreren Bereichen je eine Gruppe haben - innerhalb eines Bereichs nie zwei.
 */
export interface Sammelsendungsgruppe {
  shipmentId: string;
  parent: ShipmentParentProjektion;
  bereich: string;
  mitglieder: Order[];
}

/**
 * Eine Position in der Reihenfolge eines Bereichs: eine gewoehnliche Bestellzeile oder eine Klammer
 * (siehe `gruppiereSammelsendungen#abfolge`).
 */
export type Boardzeile =
  | { art: "einzeln"; bestellung: Order; bereich: string }
  | { art: "sammelsendung"; gruppe: Sammelsendungsgruppe };

/** Schluessel des Aufklappzustands einer Klammer: je (Bereich, Sendung). */
const sammelsendungsSchluessel = (gruppe: Pick<Sammelsendungsgruppe, "bereich" | "shipmentId">): string =>
  `${gruppe.bereich}::${gruppe.shipmentId}`;

/**
 * Task 6 (Sammelsendung-Elternzeile): teilt die Bestellungen in Sammelsendungen und den Rest.
 *
 * Gruppiert wird AUSSCHLIESSLICH ueber `shipmentParent.shipmentId` (gebildet aus
 * `shipments.dsv_shipment_no`/`order_shipments`) - NIE ueber `shipmentOrderGroup` (der Name luegt:
 * er meint den Split, nicht die Sendung) und mit keiner zweiten Gruppen-ID.
 *
 * Nutzerentscheidung 29.09.2026: eine Sendung erscheint als Klammer in JEDEM Bereich, in dem
 * mindestens eine ihrer sichtbaren Bestellungen liegt (`bereichVon`), jeweils nur mit den Kindern
 * dieses Bereichs. Schwelle ist die Mitgliederzahl der SENDUNG (`shipmentParent.memberCount >= 2`),
 * nicht die der sichtbaren Zeilen - ein gefiltertes Einzelkind behaelt so seinen Sendungskontext.
 * Bestellungen ohne `shipmentParent` und Sendungen mit nur einem Mitglied bleiben gewoehnliche
 * Zeilen (`einzeln`). Die Reihenfolge der Eingabe bleibt erhalten, in beiden Listen.
 *
 * Nutzerentscheidung 16.10.2026 (Damian): Klammern werden NICHT mehr an den Anfang ihres Bereichs
 * gestellt. `abfolge` ist die gemeinsame Liste: eine Klammer steht an der Stelle, an der ihr ERSTES
 * Mitglied (in der Eingabereihenfolge, also der Spaltensortierung) stuende; weitere Mitglieder
 * erzeugen keine eigene Position. Einzelzeilen behalten ihre Eingabereihenfolge.
 */
export function gruppiereSammelsendungen(
  bestellungen: readonly Order[],
  bereichVon: (bestellung: Order) => string,
): {
  einzeln: Order[];
  sammelsendungen: Sammelsendungsgruppe[];
  abfolge: Boardzeile[];
} {
  const nachSchluessel = new Map<string, Sammelsendungsgruppe>();
  const einzeln: Order[] = [];
  const abfolge: Boardzeile[] = [];
  for (const bestellung of bestellungen) {
    const parent = bestellung.shipmentParent;
    // Eine in JTL geloeschte Bestellung ist kein Mitglied mehr: ihre Projektion traegt zwar den
    // memberCount der aktiven Kinder, eine Klammer mit ihr zeigte aber mehr Zeilen als Mitglieder.
    // Sie bleibt Einzelzeile (der Hinweis auf die Sendung kommt aus dem shipmentParent der Zeile).
    if (!parent || parent.memberCount < 2 || orderListZustand(bestellung) === "deleted") {
      einzeln.push(bestellung);
      abfolge.push({ art: "einzeln", bestellung, bereich: bereichVon(bestellung) });
      continue;
    }
    const bereich = bereichVon(bestellung);
    const schluessel = sammelsendungsSchluessel({ bereich, shipmentId: parent.shipmentId });
    const vorhanden = nachSchluessel.get(schluessel);
    if (vorhanden) vorhanden.mitglieder.push(bestellung);
    else {
      const gruppe: Sammelsendungsgruppe = { shipmentId: parent.shipmentId, parent, bereich, mitglieder: [bestellung] };
      nachSchluessel.set(schluessel, gruppe);
      abfolge.push({ art: "sammelsendung", gruppe });
    }
  }
  return { einzeln, sammelsendungen: Array.from(nachSchluessel.values()), abfolge };
}

export interface BoardSeiteProps {
  /**
   * Die `id` des WURZELKNOTENS dieser Seite - im Bestand `#board`.
   *
   * ABWEICHUNG E (Task 13, behoben im Gesamtreview 2026-08-11): bis hierher gab es diese Prop
   * nicht, und `components/App/App.tsx` legte stattdessen ein eigenes `<div id="board">` UM diese
   * Komponente. Der Bestand kennt diese Verschachtelung nicht - `renderBoard` setzt die Klasse AUF
   * den Behaelter selbst (`board.className = 'grouped-order-list'`, index.html:6994), und das CSS
   * greift genau so (`#board.grouped-order-list`, index.html:1975). Die SPA hatte damit einen
   * klassenlosen Knoten zu viel und die Klasse eine Ebene zu tief.
   *
   * Weil die Ganzseiten-Signatur keine `id` fuehrt, erschien dieser zusaetzliche Knoten in jeder
   * Aufnahme als blanker `<div>` - und war damit die ERSTE Abweichung in JEDEM Ganzseitenvergleich
   * (board-standard, board-gruppe-zu, board-suche, board-listeneditor, costs-standard,
   * overview-standard, payments-standard). Kein Vergleich kam ueber sie hinaus.
   *
   * Als PROP und nicht fest verdrahtet: die Struktur-Paritaetstests
   * (`test/komponenten/Board/boardStruktur.test.tsx`) zeichnen diese Komponente EINZELN und
   * verankern ihren Ausschnitt an `.grouped-order-list` - die Klasse gehoert also hierher, die
   * `id` dagegen dem Aufrufer, der weiss, in welchem Seitenbehaelter er steht.
   */
  id?: string;
  bestellungen: readonly Order[];
  lieferanten: readonly Supplier[] | null | undefined;
  definitionen: StatusDefinitionen;
  /** `DB.orderListColumns`. */
  spalten?: readonly OrderListColumn[] | null;
  /** `ORDER_LIST_WIDTHS` aus `localStorage` (`hubOrderListWidths`). */
  breiten?: OrderListWidths;
  /** `BOARD_SECTION_COLLAPSED` aus `localStorage` (`hubBoardSections`). */
  eingeklappt?: Readonly<Record<string, unknown>>;
  /**
   * Der EU/China-Laenderfilter, seit dem Auftrag „EU/China-Laenderfilter in die App-Kopfzeile
   * verschieben" app-weiter Zustand aus `App.tsx` (`state/arbeitsplatz.ts`, `hubCountryFilter`) -
   * siehe Kopfkommentar oben, „Laenderfilter: seit 2026-08-27 app-weiter Zustand".
   *
   * PFLICHT seit der Pruefrunde 2 (Befund W1): bis dahin optional mit der Vorgabe „beide Regionen
   * sichtbar" - eine vergessene Uebergabe in `App.tsx` haette dadurch KEINEN Testfall rot werden
   * lassen (jede Seiten-Testsuite uebergab die Prop ohnehin selbst direkt). Ohne `?` faengt der
   * Compiler eine solche Luecke jetzt in `App.tsx` selbst ab. `lib/laender.ts#LAENDERFILTER_VORGABE`
   * bleibt als defensiver Rueckfall im Funktionskopf unten (siehe dort) - fuer typkorrekte Aufrufer
   * unerreichbar, aber falls doch je ein ungetypter Aufrufer die Prop ausliesse.
   */
  laenderFilter: LaenderFilter;
  /**
   * Der Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub",
   * 2026-08-27, app-weiter Zustand aus `App.tsx`) - wendet
   * `lib/transportart.ts#istBestellungNachTransportartFilterSichtbar` an derselben Stelle wie
   * `laenderFilter` an.
   *
   * PFLICHT seit der Nachbesserung zum Transportart-Filter (nach Commit c9da74e) - bis dahin OPTIONAL mit dem Argument, dieser
   * Filter sei neu und solle keinen bestehenden Testaufruf zu einer Pflichtangabe zwingen. Genau das
   * war Befund W1 bei `laenderFilter` (siehe Kommentar am selben Feld oben) wortwoertlich noch
   * einmal: eine vergessene Uebergabe in `App.tsx` haette hier NICHTS geworfen (jede Seite haette
   * defensiv „beide Transportarten sichtbar" gezeigt) und waere in KEINEM der bestehenden
   * Seiten-Testfaelle aufgefallen, weil die die Prop selbst direkt uebergeben. Ohne `?` faengt der
   * Compiler eine solche Luecke jetzt in `App.tsx` selbst ab, wie bei `laenderFilter`.
   * `lib/transportart.ts#TRANSPORTARTFILTER_VORGABE` bleibt als defensiver Rueckfall im
   * Funktionskopf unten (siehe dort) - fuer typkorrekte Aufrufer unerreichbar, aber falls doch je
   * ein ungetypter Aufrufer die Prop ausliesse.
   */
  transportartFilter: TransportartFilter;
  /**
   * Rechte des angemeldeten Nutzers. Von der ANZEIGE nicht gelesen; seit dem 2026-08-15 entscheidet
   * `edit_orders` hier aber, ob ein Zellenklick den Editor oeffnet - siehe Kopfkommentar.
   */
  rechte?: ReadonlySet<string>;
  /**
   * `openDrawer(id)`, index.html:7759. Das optionale `ziel` (Reiter, Abschnitt) kommt NUR von einer
   * nicht aenderbaren Zelle (`zelleAngeklickt`, `lib/zellZiel.ts`); ein Zeilenklick ruft weiter
   * `onOeffnen(id)` mit genau einem Argument.
   */
  onOeffnen?: (id: string, ziel?: DrawerZiel) => void;
  /** `toggleBoardSection(id)`, index.html:7029 - schreibt `hubBoardSections`. */
  onEingeklappt?: (gruppenId: string) => void;
  onKontextmenue?: (ereignis: React.MouseEvent, id: string) => void;
  /**
   * `commitOrderListEdit(id, key, value)` (index.html:7628) in reiner Form: der Schluessel und der
   * uebernommene ROHTEXT. Was daraus wird - welches Feld, welcher Endpunkt, welche Ablehnung -
   * entscheidet `lib/orderListEditor.ts#orderListSchreibweg`, angewandt vom Aufrufer; das Schreiben
   * und die Meldung bleiben dort, wie ueberall in diesem Modul.
   *
   * Das GANZE Anstossen des Editors (`startOrderListEdit`, :7612) liegt dagegen HIER: der Bestand
   * haelt es in einem Modul-Global, das genau so lange lebt wie die geladene Seite - also im
   * Komponentenzustand, wie Suche, Sortierung und Spaltenfilter darueber. Der Aufrufer erfaehrt
   * davon nichts, er bekommt erst das Ergebnis.
   */
  onZelleAendern?: (id: string, key: string, wert: string) => void;
  onSpaltenEditor?: () => void;
  onBreitenZuruecksetzen?: () => void;
  /**
   * Nachtrag 2026-08-11 (Auftraggeber-Entscheidung, Task-3-Brief): reine Durchreichung an
   * `Werkzeugleiste` - siehe dortiger Kopfkommentar. `BoardSeite` selbst wertet `rechte` weiterhin
   * NICHT aus (siehe Kopfkommentar oben); der Aufrufer berechnet die Sperre und reicht sie fertig
   * herein, damit diese Aussage wahr bleibt.
   */
  spaltenEditorGesperrt?: boolean;
  /**
   * `startOrderListColumnResize` / `resizeOrderListColumnByKey` (index.html:7339) - Task 14 hat den
   * Breitengriff verdrahtet. Die Bedienung liegt in `SpaltenBreiten.ts`, damit `BoardSeite` keinen
   * eigenen Aussenbezug auf `localStorage` bekommt (die Breiten kommen als Prop herein).
   */
  onBreiteZiehen?: (ereignis: React.PointerEvent<HTMLElement>, schluessel: string) => void;
  onBreiteTaste?: (ereignis: React.KeyboardEvent<HTMLElement>, schluessel: string) => void;
  /**
   * `moveOrderToBoardGroup(id, groupId)` (index.html:6629-6636) in reiner Form: nur der Patch, den
   * das Ziehen schreibt (`{ boardGroupOverride }`) - `save()`/`renderBoard()` bleiben beim Aufrufer.
   * Task 15.
   */
  onAendern?: (id: string, patch: { boardGroupOverride: string }) => void;
  /**
   * Auftrag "Sammel-Bereichswechsel per Drag-and-Drop": Sammel-Bereichswechsel: `ids` ist die
   * GESAMTE Auswahl zum Zeitpunkt des Ablegens (nicht nur die gezogene Zeile), `patch` derselbe
   * `{ boardGroupOverride }` wie bei `onAendern`. Nur gerufen, wenn die gezogene Zeile TEIL einer
   * Mehrfachauswahl (Groesse > 1) ist - sonst weiterhin `onAendern` fuer die Einzelzeile,
   * unveraendert (siehe `onDrop`-Handler unten).
   */
  onSammelAendern?: (ids: readonly string[], patch: { boardGroupOverride: string }) => void;
  /** `toast(...)` nach einem erfolgreichen Ablegen (index.html:6634). Task 15. */
  onMeldung?: (text: string) => void;
  /**
   * Auftrag "Sammel-Statuswechsel" (2026-09-01): gerufen mit dem NEUEN Inhalt von `ausgewaehlt`
   * (als Array), an JEDER Stelle, an der `setzeAusgewaehlt(...)` bereits laeuft (Strg-Klick,
   * Shift-Bereich, Leeren bei einfachem Klick, Escape) - siehe `setzeAusgewaehltUndMelden` unten.
   * `ausgewaehlt` selbst bleibt weiterhin LOKALER, fluechtiger UI-Zustand dieser Komponente (siehe
   * Kopfkommentar am State-Hook); diese Prop ist die einzige Naht nach aussen, damit `App.tsx` eine
   * eigene, kleine Kopie fuehren kann (Grund: `KontextMenue` ist eine EXTERNE Naht, siehe dortiger
   * Kopfkommentar, und muss wissen, ob die rechtsgeklickte Zeile Teil einer Mehrfachauswahl ist).
   */
  onAuswahlGeaendert?: (ids: readonly string[]) => void;
  /**
   * Auftrag "Sammel-Statuswechsel": Klick auf den neuen Werkzeugleiste-Knopf „Status ändern ({N})"
   * (`Werkzeugleiste.tsx`) - gerufen mit `Array.from(ausgewaehlt)`. Der Aufrufer (`App.tsx`) darf die
   * mitgegebenen Ids ignorieren und stattdessen seine eigene, per `onAuswahlGeaendert` synchron
   * gehaltene Kopie lesen (siehe dortiger Kopfkommentar) - beide Wege meinen dieselbe, stets aktuelle
   * Auswahl.
   */
  onSammelStatusAnfordern?: (ids: readonly string[]) => void;
  /** Übergibt eine prüfbar gruppierbare Auswahl an den App-Schreibweg. */
  onBestellgruppeAnlegen?: (orderIds: string[]) => void;
  /**
   * Nachbesserung (Pruefung e9f0edb, Wichtig 2): `SammelStatusDialog` haengt einen EIGENEN,
   * unabhaengigen Escape-Listener an `document` (schliesst NUR den Dialog, siehe dortiger
   * Kopfkommentar). Ohne diese Sperre leert der Escape-Listener HIER UNTEN GLEICHZEITIG die
   * Mehrfachauswahl - bei 40 Zeilen muss dann von vorn markiert werden, obwohl der Nutzer nur den
   * Dialog abbrechen wollte. Das widerspricht der bewussten Entscheidung, die Auswahl nach einem
   * ERFOLGREICHEN Schreiben zu BEHALTEN (siehe `App.tsx#sammelStatusAendern`) - ein Abbruch raeumte
   * sonst mehr auf als ein Erfolg. `true`: der Escape-Effekt unten ignoriert den Tastendruck komplett
   * (leert NICHT, meldet NICHT ueber `onAuswahlGeaendert`). `App.tsx` reicht hier den offen/
   * geschlossen-Zustand des `SammelStatusDialog` durch.
   */
  escapeAuswahlGesperrt?: boolean;
  /**
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Wichtig 1, 2026-09-02): das Signal, mit dem `App.tsx` diese
   * Auswahl von AUSSEN leert - der Geisterzeilen-Fix in `App.tsx#sammelStatusAendern` laedt jede
   * erfolgreich geschriebene Bestellung unter ihrer URSPRUENGLICH GESENDETEN Id nach, `listeErsetzen`
   * traegt den nachgeladenen Datensatz danach aber ggf. eine ANDERE `id` (die neu aufgeloeste uuid
   * einer Bestellung ohne eigene `order_extras`-Zeile, der Regelfall). Diese Prop hier (`ausgewaehlt`)
   * haengt weiterhin an der ALTEN Kennung - ohne einen Weg von aussen, sie zu leeren, verwaist die
   * Markierung UNSICHTBAR (die Zaehler-Anzeige bleibt gleich, nur `order-list-row-selected` fehlt an
   * der betroffenen Zeile), ein zweiter Sammelwechsel sendet die verwaiste Id dann nicht mehr sinnvoll
   * mit.
   *
   * BAUART: ein Zaehler statt eines Booleans - `App.tsx` zaehlt ihn NACH jedem erfolgreichen
   * Sammel-Statuswechsel hoch (`setAuswahlLeerenSignal((n) => n + 1)`), unabhaengig vom vorherigen
   * Wert. Ein Boolean (`auswahlLeeren?: boolean`) kollidierte mit `ausgewaehlt`/`anker`: zwei
   * aufeinanderfolgende Sammelwechsel, zwischen denen der Wert nie auf `false` zurueckgesetzt wuerde,
   * blieben beim zweiten Mal wirkungslos (derselbe `true`-Wert loest in React kein zweites
   * `useEffect`-Feuern aus) - ein Zaehler aendert sich dagegen bei JEDEM Erfolg garantiert. Verglichen
   * wird gegen den ZULETZT GESEHENEN Wert (Ref, siehe Effekt unten), NICHT gegen einen festen
   * Startwert - so loest weder das allererste Zeichnen noch ein unveraenderter erneuter Render den
   * Effekt versehentlich aus (anders als ein simples `useEffect(() => leeren(), [signal])` ohne
   * Referenzvergleich, das bei jedem Mount einmal feuerte, selbst wenn `App.tsx` mit `signal={0}`
   * startet).
   */
  auswahlLeerenSignal?: number;
  /**
   * Task 6 (Sammelsendung-Elternzeile): Klick auf den Navigationsknopf einer `Sammelsendungszeile`,
   * gerufen mit der `shipmentId` der Sendung. Der Drawer dahinter ist Task 7.
   */
  onSammelsendungOeffnen?: (shipmentId: string) => void;
  /**
   * Kontotyp "Nur lesen" (`state/nurLeser.ts`): keine ziehbaren Zeilen, kein Drop-Ergebnis, kein Knopf
   * „Status ändern ({N})" und keiner „Bestellgruppe anlegen ({N})" - siehe Kopfkommentar. Ohne Angabe: unveraendert.
   */
  nurLeser?: boolean;
  /**
   * Plan 2026-10-31, Task 6: das Faelligkeitsdatum direkt an der Elternzeile einer Sammelsendung gesetzt (`wert`
   * "YYYY-MM-DD") oder geleert (`null`). Gerufen mit der `rowVersion` der zentralen Werte, auf der die Zelle stand; das
   * Schreiben (`gemeinsameWerteSchreiben`), die Meldung und das Nachladen der Kinder gehoeren dem Aufrufer. Die Zelle ist
   * bearbeitbar mit dem Recht `edit_orders`, unabhaengig davon, ob diese Prop gesetzt ist.
   */
  onSammelFaelligkeitSetzen?: (shipmentId: string, rowVersion: number, wert: string | null) => void;
}

export function BoardSeite({
  id,
  bestellungen,
  lieferanten,
  definitionen,
  spalten,
  breiten = {},
  eingeklappt = {},
  laenderFilter = LAENDERFILTER_VORGABE,
  transportartFilter = TRANSPORTARTFILTER_VORGABE,
  rechte,
  onOeffnen,
  onEingeklappt,
  onKontextmenue,
  onZelleAendern,
  onSpaltenEditor,
  onBreitenZuruecksetzen,
  onBreiteZiehen,
  onAendern,
  onSammelAendern,
  onMeldung,
  onBreiteTaste,
  spaltenEditorGesperrt,
  onAuswahlGeaendert,
  onSammelStatusAnfordern,
  onBestellgruppeAnlegen,
  escapeAuswahlGesperrt,
  auswahlLeerenSignal,
  onSammelsendungOeffnen,
  nurLeser = false,
  onSammelFaelligkeitSetzen,
}: BoardSeiteProps) {
  // Suche, Archiv, Sortierung und Spaltenfilter leben in der ADRESSE (Spezifikation § 3.3), damit
  // sich eine gefilterte Ansicht verschicken laesst und ein Neuladen sie behaelt.
  //
  // ERSETZEN, nicht schieben: die Zurueck-Taste soll dorthin fuehren, wo man WAR, nicht durch jeden
  // Tastendruck der Suche (§ 4).
  const { ansicht, setzeAnsicht } = useAnsicht();
  const { q: suche, states, sort, dir, spaltenfilter, neu } = ansicht.board;
  const sortierung = useMemo(() => ({ key: sort, direction: dir }), [sort, dir]);
  // FUNKTIONSFORM (`alt => …`), nicht ein fertiges Objekt: der verzoegerte Suchschreiber unten
  // (Schritt 4) kann feuern, NACHDEM Archiv/Sortierung/Spaltenfilter sich zwischenzeitlich schon
  // geaendert haben - ein fertiges Objekt schluesse auf dem Stand VOR dieser Aenderung und wuerfe
  // sie beim Feuern wieder weg. Die Funktionsform liest immer den Stand, auf dem `setzeAnsicht`
  // GERADE rechnet (`useAnsicht.ts`, Kommentar bei `aktuellerStand`), nie einen veralteten
  // Ausschnitt (siehe derselbe Befund/dieselbe Loesung in `LieferantenSeite.tsx`).
  const setzeBoard = (teil: Partial<typeof ansicht.board>): void =>
    setzeAnsicht((alt) => ({ board: { ...alt.board, ...teil } }), { ersetzen: true });
  // AUFTRAG 07 (E3): statt `<select>` mit einem Wert jetzt drei Kontrollkaestchen, die je EINEN
  // Zustand ein-/ausschalten. Die Menge wird ueber `normalisiereBoardStates` kanonisiert (feste
  // Reihenfolge `active, archived, deleted`, Duplikate weg) - sonst waere die abgelegte Menge von
  // der Anklick-Reihenfolge abhaengig, und die Adresse (und damit der E1-Schluessel, `App.tsx`)
  // aendere sich, obwohl dieselbe Sicht gemeint ist.
  const setzeStates = (neueStates: BoardStateSelection): void => setzeBoard({ states: neueStates });
  const setzeSortierung = (wert: { key: string; direction: "asc" | "desc" }): void =>
    setzeBoard({ sort: wert.key, dir: wert.direction });
  const setzeSpaltenfilter = (wert: Spaltenfilter): void => setzeBoard({ spaltenfilter: wert });
  const setzeNeu = (an: boolean): void => setzeBoard({ neu: an });

  // Der Sofortwert fuers Eingabefeld UND fuer die Filterung (`query` unten): nur der SCHREIBER in
  // die Adresse ist entprellt (`useEntprelltenAdresswert`), nicht die sichtbare Liste - sonst
  // haenge die Trefferliste 300 ms hinter jedem Tastendruck her.
  const [sucheRoh, setzeSucheRoh] = useEntprelltenAdresswert(suche, (wert) => setzeBoard({ q: wert }));

  // Task 6: aufgeklappte Sammelsendungs-Klammern, je (Bereich, Sendung) - siehe sammelsendungsSchluessel.
  const [aufgeklappt, setzeAufgeklappt] = useState<ReadonlySet<string>>(() => new Set());

  // `orderListEditCell` (index.html:4651) - dasselbe "genau eine Zelle, seitenlang" wie im Bestand.
  const [bearbeitung, setzeBearbeitung] = useState<{ id: string; key: string } | null>(null);

  /**
   * Auftrag "Filterfenster im Bestellboard - nur eins gleichzeitig offen" (2026-09-04): welche
   * Spalte gerade ihr Filtermenue offen hat - `null` heisst "keins". Bis hierher verwaltete jede
   * `Spaltenkopf`-Instanz ihren `offen`-Zustand rein lokal (`useState` dort, siehe deren Eigenheit 6);
   * zwei Menues konnten deshalb gleichzeitig offen stehen. Der Zustand wandert hierher, EINE Ebene
   * ueber alle Spalten, weil genau hier - und nur hier - `<Spaltenkopf>` fuer jede Spalte gezeichnet
   * wird (kein eigener Kontext noetig fuer eine Koordination, die nirgendwo sonst gebraucht wird).
   * `Spaltenkopf` erhaelt `offen`/`onOffenAendern` seither als Props statt eines eigenen States - das
   * `<details>` bleibt darueber QUASI-GESTEUERT (ein `open`-Prop von hier, siehe dortiger Kommentar).
   */
  const [geoeffneterSpaltenfilter, setzeGeoeffneterSpaltenfilter] = useState<string | null>(null);
  /**
   * `vorher === key ? null : vorher` ist tragend: ein SCHLIESSEN-Ereignis der gerade NICHT (mehr)
   * aktiven Spalte darf den frisch geoeffneten Filter einer ANDEREN Spalte nicht wieder zuruecksetzen.
   * Das passiert real - siehe `Spaltenkopf`: das Oeffnen von B setzt diesen State direkt auf "B", der
   * dadurch erzwungene `open=false` an A's `<details>` loest dort ASYNCHRON (ein Tick spaeter) noch
   * ein eigenes `onToggle` mit `open=false` aus, das ohne diese Absicherung "B" wieder auf `null`
   * zoege.
   */
  const spaltenfilterOffenAendern = useCallback(
    (key: string, offenNeu: boolean): void =>
      setzeGeoeffneterSpaltenfilter((vorher) => {
        if (offenNeu) return key;
        return vorher === key ? null : vorher;
      }),
    [],
  );
  /**
   * NACHBESSERUNG (Pruefung, "bei Gelegenheit"): `<Spaltenkopf>` braucht je Spalte EINEN stabilen
   * `onOffenAendern`-Callback (der Klick-aussen-`useEffect` dort haengt/entfernt seinen
   * `document`-Listener bei JEDEM Wechsel dieser Referenz neu an, siehe dortiger Kommentar). Eine
   * inline erzeugte Closure je Spalte in der `.map(...)` unten waere bei JEDEM Render von
   * `BoardSeite` eine NEUE Funktion, selbst wenn sich nichts an der Koordination aendert - der
   * Listener wuerde dann bei jedem Render ab-/wieder angemeldet, obwohl kein Leck entsteht (das
   * Aufraeumen greift ja). Diese `Map` cacht EINE Closure je Spaltenschluessel ueber die gesamte
   * Lebensdauer von `BoardSeite` (Ref, kein Re-Init bei Re-Render) - `spaltenfilterOffenAendern`
   * selbst ist oben `useCallback`-stabil (nur die STABILE `setzeGeoeffneterSpaltenfilter`
   * geschlossen, kein sich aenderender Zustand direkt gelesen), die gecachten Closures bleiben also
   * auch inhaltlich korrekt, nicht nur referenzstabil.
   */
  const offenAendernHandlerCache = useRef(new Map<string, (offen: boolean) => void>());
  const offenAendernHandler = (key: string): ((offen: boolean) => void) => {
    let handler = offenAendernHandlerCache.current.get(key);
    if (!handler) {
      handler = (offenNeu) => spaltenfilterOffenAendern(key, offenNeu);
      offenAendernHandlerCache.current.set(key, handler);
    }
    return handler;
  };
  // Traegt die gezogene Id ueber dragstart -> drop hinweg (ZeilenUmzug.ts, Bericht B2) - ueberlebt
  // ABSICHTLICH Re-Renders innerhalb EINES Ziehvorgangs, deshalb eine Ref und kein `useState`.
  const umzugRef = useRef(neueZeilenUmzugRef());

  /**
   * Auftrag "Mehrfachauswahl" (Strg/Shift-Klick auf der Bestellübersicht), 2026-09-01: der
   * Auswahlzustand - rein FLUECHTIGER UI-Zustand, deshalb hier lokal und NICHT in `ansicht`/der
   * Adresse (die traegt geteilten, verschickbaren Zustand wie Suche/Sortierung/Filter, siehe
   * Kopfkommentar oben, "Der Datenweg"). `ausgewaehlt` haelt die markierten Order-Ids, `anker` die
   * zuletzt per Strg-/Shift-Klick beruehrte Id als Basis fuer den naechsten Shift-Bereich (siehe
   * `zeileAngeklickt` unten). Dieser Auftrag liefert NUR den Auswahlmechanismus - zwei SPAETERE
   * Auftraege (Sammel-Statuswechsel, Sammel-Drag&Drop) lesen/erweitern genau diesen Block direkt,
   * kein Export noetig.
   */
  const [ausgewaehlt, setzeAusgewaehlt] = useState<Set<string>>(() => new Set());
  const [anker, setzeAnker] = useState<string | null>(null);
  const [bestellgruppeDialogOffen, setBestellgruppeDialogOffen] = useState(false);

  /**
   * Auftrag "Sammel-Statuswechsel": EINZIGE Stelle, die `ausgewaehlt` schreibt UND `App.tsx` ueber
   * `onAuswahlGeaendert` benachrichtigt - jede der vier bisherigen `setzeAusgewaehlt(...)`-Stellen
   * (Strg-Klick, Shift-Bereich, Leeren bei einfachem Klick, Escape) laeuft seither hierueber, damit
   * keine davon die Meldung vergessen kann.
   *
   * `useCallback` seit Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung
   * beheben" (2026-09-06): `zeileAngeklickt` unten schliesst diese Funktion, und `zeileAngeklickt`
   * selbst MUSS referenzstabil bleiben, wenn sich weder `ausgewaehlt`/`anker` noch diese Funktion
   * selbst geaendert haben (sonst waere `Bestellzeile`s `React.memo` wirkungslos - siehe dortiger
   * Kommentar). Eine bei jedem Render frisch erzeugte Fassung HIER wuerde `zeileAngeklickt`s eigenes
   * `useCallback` trotz vollstaendigem Abhaengigkeitsarray dennoch bei jedem Render neu ausloesen.
   */
  const setzeAusgewaehltUndMelden = useCallback(
    (naechste: Set<string>): void => {
      setzeAusgewaehlt(naechste);
      onAuswahlGeaendert?.(Array.from(naechste));
    },
    [onAuswahlGeaendert],
  );

  /**
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Wichtig 1): leert die Auswahl, sobald `auswahlLeerenSignal`
   * sich AENDERT - siehe Kopfkommentar der Prop fuer Befund und Bauart. `auswahlLeerenSignalRef`
   * traegt den ZULETZT GESEHENEN Wert: der Vergleich `auswahlLeerenSignal === ref.current` verhindert,
   * dass der Effekt beim allerersten Zeichnen feuert (dort sind Prop und Ref-Initialwert identisch,
   * unabhaengig davon, ob `App.tsx` mit `0`, `undefined` oder einem anderen Startwert beginnt).
   */
  const auswahlLeerenSignalRef = useRef(auswahlLeerenSignal);
  useEffect(() => {
    if (auswahlLeerenSignal === auswahlLeerenSignalRef.current) return;
    auswahlLeerenSignalRef.current = auswahlLeerenSignal;
    setzeAusgewaehltUndMelden(new Set());
    setBestellgruppeDialogOffen(false);
    // `setzeAusgewaehltUndMelden` haengt an `onAuswahlGeaendert` (Prop) und `setzeAusgewaehlt`
    // (stabiler State-Setter) - beide duerfen hier fehlen, wie beim Escape-Effekt weiter unten mit
    // vergleichbarer Begruendung nicht der Fall ist: dort ist `ausgewaehlt` selbst noetig, um zu
    // wissen, OB ueberhaupt etwas zu leeren ist, hier entscheidet einzig `auswahlLeerenSignal`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auswahlLeerenSignal]);

  const columns = useMemo(
    () => activeOrderListColumns(spalten ?? undefined),
    [spalten],
  );

  const query = normSearch(sucheRoh);
  const facetOrders = useMemo(
    () =>
      bestellungen
        // REINE ABSICHTSPRUEFUNG seit Auftrag "Serverseitiger Archivfilter + Ladeanzeige"
        // (2026-08-27): den Archivfilter wendet SEIT DIESEM AUFTRAG bereits der Server an
        // (`GET /api/jtl/orders?archiv=…`, siehe `jtlBestellungen.ts`/`Speicher.tsx`) - `bestellungen`
        // sollte also schon die passende Teilmenge sein. Dieser Filter bleibt trotzdem stehen: eine
        // defensive Doppelpruefung, falls je eine andere Quelle (z. B. Zweig 1 in `Speicher.tsx`, der
        // lokale Bestands-Server ohne serverseitigen Archivfilter) ungefilterte Bestellungen liefert -
        // die Hauptlast liegt seither beim Server, nicht mehr hier.
        // AUFTRAG 07 (E2): der Server filtert nach der Zustandsmenge (`state=…` mehrfach, siehe
        // `state/adresse.ts`). Der lokale Nachlauf bleibt als ABSICHTS-/Doppelpruefung - und er
        // prueft jetzt die MENGE: bis Auftrag 06 stand hier `archiv === "all" || (archiv ===
        // "archived" ? …)`. Mit einer MENGE ist dieser Ausdruck NIE wahr (`[] === "all"` ist falsch,
        // `[] === "archived"` ebenfalls), also fielen ALLE Zeilen durch - auch archivierte und
        // geloeschte, die sich dann sogar wieder ziehen liessen. Geprueft wird deshalb gegen
        // besagt aber, dass eine geloeschte Bestellung in GENAU EINE Kategorie faellt - `deleted`
        // gewinnt dort (siehe `orderListZustand` in `lib/orderList.ts`).
        .filter((order) => states.includes(orderListZustand(order)))
        // Der Suchheuhaufen steht seit dem KI-Zugang (Stufe 1) in `lib/boardSuche.ts`: der Server
        // sucht mit derselben Funktion. `query` ist schon normalisiert (oben, einmal je Darstellung).
        .filter((order) => bestellungTrifftSuche(order, query))
        // Laenderfilter (siehe Kopfkommentar oben, „Laenderfilter: seit 2026-08-27 app-weiter
        // Zustand"): die geteilte Regel statt einer eigenen Kopie.
        .filter((order) => istBestellungNachLaenderFilterSichtbar(order, lieferanten, laenderFilter))
        // Transportart-Filter (Luftfracht/Seefracht), dieselbe Bauart, eigene geteilte Regel
        // (`lib/transportart.ts`).
        .filter((order) => istBestellungNachTransportartFilterSichtbar(order, transportartFilter)),
    [bestellungen, states, query, lieferanten, laenderFilter, transportartFilter],
  );

  // `orderListSpaltenfilterAktiv` statt eines blossen Wahrheitstests auf `value`: die Form des Werts
  // (Array/Bereich/Boolean) haengt vom Datentyp der Spalte ab (`lib/orderList.ts#Spaltenfilterwert`),
  // ein leeres Array oder ein Bereich ohne Von/Bis zaehlen NICHT als aktiv.
  const activeColumnFilters = Object.entries(spaltenfilter).filter(([key, value]) =>
    orderListSpaltenfilterAktiv(key, value),
  );

  const nachSpaltenfiltern = useMemo(
    () =>
      facetOrders.filter((order) =>
        activeColumnFilters.every(([key, value]) =>
          orderListZeilePasstFilter(order, key, value, definitionen, lieferanten),
        ),
      ),
    // `activeColumnFilters` wird bei jedem Lauf neu gebildet und taugt deshalb nicht als
    // Abhaengigkeit; `spaltenfilter` ist der tragende Zustand dahinter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facetOrders, spaltenfilter, definitionen, lieferanten],
  );

  // Feature "Neue Artikel": die Zahl im Schalter „Neu (n)" zaehlt NACH allen uebrigen Filtern (Zustand,
  // Suche, Laender, Transportart in `facetOrders`, dazu die Spaltenfilter) - sie nennt, was der Schalter
  // von der gerade sichtbaren Liste uebrig liesse. Der Neu-Filter selbst wirkt zuletzt, vor der
  // Sortierung und vor `gruppiereSammelsendungen`.
  const neuAnzahl = useMemo(() => nachSpaltenfiltern.filter(hatNeueArtikel).length, [nachSpaltenfiltern]);

  const orders = useMemo(() => {
    // Immer eine EIGENE Liste: das `sort` unten arbeitet an Ort und Stelle.
    const gefiltert = neu ? nachSpaltenfiltern.filter(hatNeueArtikel) : [...nachSpaltenfiltern];
    const direction = sortierung.direction === "asc" ? 1 : -1;
    // `sort` veraendert die Liste an Ort und Stelle - `gefiltert` ist bereits eine neue Liste aus
    // `filter`, die Eingabeliste `bestellungen` wird also nicht angetastet.
    gefiltert.sort((a, b) => {
      const av = orderListSortValue(a, sortierung.key, definitionen);
      const bv = orderListSortValue(b, sortierung.key, definitionen);
      if ((av === "" || av == null) && bv !== "" && bv != null) return 1;
      if ((bv === "" || bv == null) && av !== "" && av != null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * direction;
      return (
        String(av).localeCompare(String(bv), "de", { numeric: true, sensitivity: "base" }) * direction
      );
    });
    return gefiltert;
  }, [nachSpaltenfiltern, neu, sortierung, definitionen]);

  /**
   * Task 6 (Sammelsendung-Elternzeile): die Sammelsendungen VOR dem Status-Rendering herausloesen
   * (`gruppiereSammelsendungen`): je (Bereich, Sendung) eine Klammer. `einzeln` traegt alle
   * uebrigen Zeilen; innerhalb eines Bereichs steht jede Sendungsnummer hoechstens einmal.
   * `abfolgeJeBereich` ist die Renderreihenfolge je Bereich (Klammern an der Stelle ihres ersten
   * Mitglieds, Nutzerentscheidung 16.10.2026).
   */
  const gruppierung = useMemo(() => {
    const geteilt = gruppiereSammelsendungen(orders, (bestellung) =>
      boardGroupId(bestellung, definitionen, bestellungen),
    );
    const jeBereich = new Map<string, Sammelsendungsgruppe[]>();
    for (const gruppe of geteilt.sammelsendungen) {
      jeBereich.set(gruppe.bereich, [...(jeBereich.get(gruppe.bereich) ?? []), gruppe]);
    }
    // Die gemischte Reihenfolge je Bereich: Klammern stehen an der Stelle ihres ersten Mitglieds.
    const abfolgeJeBereich = new Map<string, Boardzeile[]>();
    for (const zeile of geteilt.abfolge) {
      const bereich = zeile.art === "einzeln" ? zeile.bereich : zeile.gruppe.bereich;
      const liste = abfolgeJeBereich.get(bereich);
      if (liste) liste.push(zeile);
      else abfolgeJeBereich.set(bereich, [zeile]);
    }
    return { ...geteilt, jeBereich, abfolgeJeBereich };
  }, [orders, definitionen, bestellungen]);

  /**
   * Auftrag "Mehrfachauswahl": die abgeflachte Liste aller SICHTBAREN Zeilen - nicht eingeklappte
   * Gruppen, in der Reihenfolge, in der die Tabelle sie unten rendert (`BOARD_GROUPS`-Reihenfolge,
   * innerhalb einer Gruppe die Sortierreihenfolge von `orders`) - die Grundlage fuer den Shift-
   * Bereich in `zeileAngeklickt`. Sie liest dieselbe `abfolgeJeBereich` wie der Render-Zweig unten
   * (Klammern und Einzelzeilen gemischt, 16.10.2026), damit Shift-Bereich und sichtbare Reihenfolge
   * nicht auseinanderlaufen.
   */
  const sichtbareZeilenIds = useMemo(() => {
    const ids: string[] = [];
    for (const group of BOARD_GROUPS) {
      if (eingeklappt[group.id]) continue;
      // Task 6 / 16.10.2026: so wie die Tabelle rendert - Klammern und Einzelzeilen gemischt, in der
      // Reihenfolge von `abfolgeJeBereich`. Die Kinder einer Klammer zaehlen nur bei AUFGEKLAPPTER
      // Klammer, zugeklappte sind nicht sichtbar.
      for (const zeile of gruppierung.abfolgeJeBereich.get(group.id) ?? []) {
        if (zeile.art === "einzeln") {
          ids.push(zeile.bestellung.id as string);
          continue;
        }
        if (!aufgeklappt.has(sammelsendungsSchluessel(zeile.gruppe))) continue;
        for (const mitglied of zeile.gruppe.mitglieder) ids.push(mitglied.id as string);
      }
    }
    return ids;
  }, [gruppierung, aufgeklappt, eingeklappt]);

  /**
   * Auftrag "Mehrfachauswahl": archivierte Zeilen sind von der Auswahl ausgeschlossen (Kopfkommentar
   * `Bestellzeile.tsx`, Eigenheit 1 - dieselbe Regel wie beim Ziehen).
   *
   * NACHBESSERUNG (G4, Pruefung f590385): vorher eine Funktion, die `bestellungen` bei JEDEM Aufruf
   * linear durchsuchte (`Array.find`) - im Shift-Zweig einmal JE ZEILE des Bereichs, also O(n·m)
   * (n Bestellungen, m Zeilen im markierten Bereich). Jetzt einmal je Render vorab als `Set`
   * gebildet (derselbe Aufbau wie `sichtbareZeilenIds` oben), das Nachschlagen selbst ist danach
   * O(1) je Zeile statt O(n).
   *
   * AUFTRAG 07 (E2/E4): in JTL geloeschte Zeilen kommen hier ZUSAE Tzlich hinzu - die Sperre gilt
   * fuer Archivierte und Geloeschte gleichermassen, denn beide koennen nicht Ziel einer Sammelaktion
   * sein (das Schreiben wuerde in JTL fehlschlagen). `orderListZustand` ist die EINE Ableitung
   * (`lib/orderList.ts`): `"deleted"` schlaegt dort `"archived"`.
   */
  const archivierteIds = useMemo(
    () =>
      new Set(
        bestellungen
          .filter((b) => b.archived || orderListZustand(b) === "deleted")
          .map((b) => b.id as string),
      ),
    [bestellungen],
  );
  useEffect(() => {
    const bereinigt = new Set(Array.from(ausgewaehlt).filter((id) => !archivierteIds.has(id)));
    if (bereinigt.size === ausgewaehlt.size) return;
    setzeAusgewaehltUndMelden(bereinigt);
    setzeAnker((vorher) => (vorher && bereinigt.has(vorher) ? vorher : null));
  }, [archivierteIds, ausgewaehlt, setzeAusgewaehltUndMelden]);
  const gruppenAuswahl = useMemo(
    () => Array.from(ausgewaehlt).map((id) => bestellungen.find((bestellung) => bestellung.id === id)).filter(
      (bestellung): bestellung is Order => bestellung !== undefined,
    ),
    [ausgewaehlt, bestellungen],
  );
  const bestellgruppeGesperrt = useMemo(() => {
    if (gruppenAuswahl.length < 2) return "Mindestens zwei Bestellungen auswählen";
    if (gruppenAuswahl.some((bestellung) => !bestellung.supplierId)) {
      return "Bestellungen ohne Lieferant können nicht gruppiert werden";
    }
    if (new Set(gruppenAuswahl.map((bestellung) => bestellung.supplierId)).size !== 1) {
      return "Nur Bestellungen desselben Lieferanten gruppieren";
    }
    if (gruppenAuswahl.some((bestellung) => bestellung.orderGroupStatus === "active")) {
      return "Bereits aktiv gruppierte Bestellungen können nicht erneut gruppiert werden";
    }
    return "";
  }, [gruppenAuswahl]);

  /**
   * Auftrag "Mehrfachauswahl" (Strg/Shift-Klick auf der Bestellübersicht): der EINE Klick-Handler
   * der Zeile, ersetzt das bisherige `onClick={() => onOeffnen?.(o.id)}` in `Bestellzeile`. Drei
   * Faelle:
   *
   * - OHNE Zusatztaste: die Auswahl leert sich (falls nicht schon leer), der Anker geht verloren -
   *   er gehoert zu einer Auswahl, die es danach nicht mehr gibt -, `onOeffnen` feuert wie bisher,
   *   UNABHAENGIG vom Archivstand (archivierte Zeilen oeffnen weiterhin normal).
   * - MIT Strg/Cmd: togglet die Zeile in `ausgewaehlt`, setzt `anker` auf diese Id, `onOeffnen`
   *   bleibt aus. Eine archivierte Zeile bleibt UNBERUEHRT (weder Toggle noch Anker) - sie kann
   *   ohnehin nicht Ziel einer Sammelaktion sein.
   * - MIT Shift: markiert den Bereich von `anker` bis zu dieser Zeile (beide Enden eingeschlossen)
   *   ueber `sichtbareZeilenIds` und ERSETZT damit die bisherige Auswahl (kein additives
   *   Shift+Strg in diesem Auftrag). Archivierte Zeilen INNERHALB eines Bereichs faerben nicht mit
   *   ein - das gilt seit NACHBESSERUNG G1 (Pruefung f590385) AUCH dann, wenn die geklickte Zeile
   *   SELBST archiviert ist: sie zaehlt weiterhin als Bereichs*grenze* (die Indexrechnung laeuft wie
   *   fuer jede andere Zeile), nur landet sie selbst - wie jede andere archivierte Zeile im Bereich -
   *   nicht in der resultierenden Auswahl. VORHER war ein Shift-Klick auf eine archivierte Zeile ein
   *   VOLLSTAENDIGES No-op (kein Bereich entstand ueberhaupt, selbst wenn Anker und Ziel mehrere
   *   aktive Zeilen dazwischen einschlossen) - das widersprach der urspruenglichen Vorgabe, nach der
   *   nur die archivierten Zeilen SELBST von der Auswahl ausgenommen sind, nicht der ganze Bereich.
   *   Ohne Anker (noch kein vorheriger Strg-/Shift-Klick) oder wenn der Anker ODER das Ziel
   *   zwischenzeitlich aus den sichtbaren Zeilen gefallen ist (z. B. die Gruppe wurde eingeklappt),
   *   verhaelt sich Shift wie ein einfacher Strg-Klick - derselbe Fallback, den der Auftragsbrief
   *   fuer den Anker-losen Fall nennt; DORT bleibt eine archivierte Zielzeile weiterhin wirkungslos
   *   (kein Bereich, den sie begrenzen koennte - der Toggle-Fallback wuerde sie sonst direkt und
   *   ungefiltert in die Auswahl aufnehmen). Der Anker selbst bewegt sich waehrend einer LAUFENDEN
   *   Bereichsauswahl NICHT mit: er bleibt die "Basis fuer den naechsten Shift-Bereich"
   *   (Auftragsbrief) - erst ein neuer Strg-Klick oder einer der beiden Anker-losen Faelle setzt ihn
   *   neu.
   */
  /**
   * Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung beheben" (2026-09-06):
   * `useCallback` mit VOLLSTAENDIGEM Abhaengigkeitsarray - `ausgewaehlt`/`anker` werden hier
   * GELESEN und muessen deshalb als Abhaengigkeit stehen (ein zu knappes Array friert sonst eine
   * veraltete Auswahl/einen veralteten Anker ein). Das kostet NICHTS fuer den Fix: das Oeffnen eines
   * Spaltenfilters (`geoeffneterSpaltenfilter`) aendert weder `ausgewaehlt` noch `anker` noch eine
   * der uebrigen Abhaengigkeiten - `zeileAngeklickt` bleibt fuer GENAU DIESE Aenderung referenzstabil,
   * und `Bestellzeile`s `React.memo` (siehe dortiger Kommentar) kann jede Zeile ueberspringen. Aendert
   * sich dagegen die Auswahl selbst (Strg-/Shift-Klick auf einer Zeile), bekommt JEDE Zeile
   * zwangslaeufig eine neue `onZeileAngeklickt`-Referenz und rendert erneut - das ist ein separates,
   * nicht Teil dieses Auftrags geloestes Verhalten (siehe Auftragsdatei, Abschnitt "Ausdruecklich
   * NICHT Teil dieses Auftrags"), keine Regression durch diesen Fix.
   */
  /**
   * Der EINFACHE Klick auf eine Zeile ODER auf eine Zelle, die man im Grid nicht aendern kann: die
   * Auswahl leert sich (falls nicht schon leer), der Anker geht verloren, der Drawer oeffnet -
   * UNABHAENGIG vom Archivstand. Mit `ziel` (nur Zellen) oeffnet er an der Stelle des Feldes.
   * Ohne `ziel` wird `onOeffnen` mit genau EINEM Argument gerufen, wie vor dem Auftrag.
   */
  const zeileOeffnen = useCallback(
    (id: string, ziel?: DrawerZiel): void => {
      if (ausgewaehlt.size) setzeAusgewaehltUndMelden(new Set());
      setzeAnker(null);
      if (ziel) onOeffnen?.(id, ziel);
      else onOeffnen?.(id);
    },
    [ausgewaehlt, onOeffnen, setzeAusgewaehltUndMelden],
  );

  const zeileAngeklickt = useCallback(
    (ereignis: React.MouseEvent, id: string): void => {
      const toggeln = (menge: Set<string>): Set<string> => {
        const neu = new Set(menge);
        if (neu.has(id)) neu.delete(id);
        else neu.add(id);
        return neu;
      };

      if (ereignis.ctrlKey || ereignis.metaKey) {
        if (archivierteIds.has(id)) return;
        setzeAusgewaehltUndMelden(toggeln(ausgewaehlt));
        setzeAnker(id);
        return;
      }

      if (ereignis.shiftKey) {
        const startIdx = anker == null ? -1 : sichtbareZeilenIds.indexOf(anker);
        const endIdx = sichtbareZeilenIds.indexOf(id);
        if (startIdx < 0 || endIdx < 0) {
          // Anker-loser Fallback (kein Anker, oder Anker/Ziel nicht (mehr) sichtbar): es entsteht
          // KEIN Bereich, der eine archivierte Zielzeile begrenzen koennte - der Toggle unten wuerde
          // `id` sonst UNGEFILTERT in die Auswahl aufnehmen. Eine archivierte Zielzeile bleibt
          // deshalb GENAU HIER weiterhin wirkungslos (Eigenheit 1) - anders als im Bereichs-Zweig
          // unten, wo sie die Bereichsgrenze bilden darf (NACHBESSERUNG G1, siehe Docblock oben).
          if (archivierteIds.has(id)) return;
          setzeAusgewaehltUndMelden(toggeln(ausgewaehlt));
          setzeAnker(id);
          return;
        }
        const [lo, hi] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        const bereich = sichtbareZeilenIds.slice(lo, hi + 1).filter((rid) => !archivierteIds.has(rid));
        setzeAusgewaehltUndMelden(new Set(bereich));
        return;
      }

      zeileOeffnen(id);
    },
    [archivierteIds, ausgewaehlt, anker, sichtbareZeilenIds, zeileOeffnen, setzeAusgewaehltUndMelden],
  );

  // Auftrag "Mehrfachauswahl": Escape leert die Auswahl - global, wie `KontextMenue.tsx`s
  // `document.addEventListener("keydown", ...)` (dort Zeilen 132-149): die Auswahl setzt kein
  // fokussiertes Element voraus (die Tastatur soll unabhaengig davon wirken, wo der Fokus gerade
  // steht).
  //
  // Auftrag "Sammel-Statuswechsel": hier stand bis dahin die FUNKTIONALE Form
  // (`setzeAusgewaehlt((alt) => (alt.size ? new Set() : alt))`) mit leerem Abhaengigkeitsarray -
  // die spart die Abhaengigkeit von `ausgewaehlt`, kann aber `onAuswahlGeaendert` nicht sauber
  // rufen: der neue Wert entsteht ERST innerhalb des Updaters, ausserhalb ist er nicht bekannt (und
  // ein Seiteneffekt IM Updater liefe unter React 18 Strict Mode potenziell doppelt). Jetzt die
  // NICHT-funktionale Form mit `ausgewaehlt` als Abhaengigkeit - dasselbe Muster wie
  // `KontextMenue.tsx`s eigener Escape-Effekt (dort `[offen, onSchliessen]`): der Effekt haengt den
  // Listener bei jeder Aenderung der Auswahl neu ein, beobachtbar identisch.
  //
  // Nachbesserung (Pruefung e9f0edb, Wichtig 2): `escapeAuswahlGesperrt` unterdrueckt GENAU diesen
  // einen Tastendruck vollstaendig (kein Leeren, kein `onAuswahlGeaendert`) - siehe Kopfkommentar
  // der Prop. `App.tsx` setzt sie, waehrend `SammelStatusDialog` offen ist: dessen EIGENER,
  // unabhaengiger Escape-Listener soll den Dialog schliessen, ohne dass DIESER Listener hier
  // gleichzeitig die muehsam markierte Auswahl mit aufraeumt.
  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key !== "Escape") return;
      if (bestellgruppeDialogOffen) {
        setBestellgruppeDialogOffen(false);
        return;
      }
      if (!ausgewaehlt.size || escapeAuswahlGesperrt) return;
      setzeAusgewaehltUndMelden(new Set());
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [ausgewaehlt, onAuswahlGeaendert, escapeAuswahlGesperrt, bestellgruppeDialogOffen]);

  // index.html:7369 - derselbe Schluessel kippt die Richtung, ein neuer setzt sie auf `asc`
  // (ausser bei `value`: dort auf `desc`). `sortierung` kommt frisch aus der Adresse - kein `prev`
  // noetig, der Adresszugriff kennt keine Aktualisierungsfunktion (§ 3.3, Aufgabe 4, Schritt 3).
  const sortieren = (key: string) =>
    setzeSortierung(
      sortierung.key === key
        ? { key, direction: sortierung.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "value" ? "desc" : "asc" },
    );

  // `edit_orders` EINMAL im Rumpf (Fix-Runde 1 zu Task 6): die Zellen der Kinderzeilen (`zelleAngeklickt` unten) und die
  // Faelligkeitszelle der Elternzeile (`sammelzeile`) fragen dasselbe.
  const darfBearbeiten = hatRecht(rechte ?? new Set<string>(), "edit_orders");
  /**
   * `startOrderListEdit(orderId, key)` (index.html:7612) - mit einer Weiche davor, die der Bestand
   * nicht braucht: zwei Spalten (`name`, `supplier`) haben in der SPA keinen Schreibweg. Der Bestand
   * bietet dort trotzdem ein Feld an, weil er nur in den Speicher schreibt; hier scheiterte die
   * Uebernahme zwangslaeufig am Server.
   *
   * `etd`/`eta` traegen DENSELBEN Zweig fuer den Fall einer verknuepften DSV-Sendung
   * (`orderListEditorRegel`/`DSV_GEFUEHRT`, `lib/orderListEditor.ts`) - der Mechanismus bleibt
   * bewusst stehen, weil er die Anzeigeseite einer Backend-Regel ist (`istDsvGefuehrt`,
   * `backend/src/domaene/extras.ts`: eine Uebernahme MUESSTE dort mit 409 scheitern). EHRLICH
   * gemacht seit dem Auftrag „Sendungsabruf beim App-Start entfernen" (2026-08-22): die Bestellliste
   * hat keine Quelle fuer die verknuepfte Sendung mehr (siehe Aufruf unten, `null` statt einer
   * Aufloesung) - dieser Zweig feuert hier deshalb NIE, unabhaengig vom tatsaechlichen
   * Verknuepfungsstand der Bestellung. Das ist keine Verschlechterung: er feuerte auch vorher nie,
   * weil die einzige Quelle des Zeigers (`order.shipmentId`) nie gesetzt wurde.
   *
   * Statt eines Feldes, das nicht halten kann, was es verspricht, oeffnet der Klick seit dem Auftrag
   * "Klick auf nicht aenderbare Grid-Zelle oeffnet die Bestellung" (2026-10-04) die Bestellung an
   * der Stelle des Feldes (`lib/zellZiel.ts`); die Begruendung steht als `title` an der Zelle
   * (`Bestellzeile.tsx`). Dasselbe gilt fuer JEDE Zelle, wenn `edit_orders` fehlt. Die
   * anklickbare Huelle bleibt in allen Faellen unveraendert (DOM und Baselines also auch), siehe
   * `lib/orderListEditor.ts`.
   */
  /**
   * Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung beheben" (2026-09-06):
   * `useCallback` - dieselbe Begruendung wie bei `zeileAngeklickt` oben: `bestellungen` und `zeileOeffnen` sind Props (bleiben
   * referenzstabil, solange der Aufrufer von `BoardSeite` sie nicht selbst neu erzeugt), `darfBearbeiten` (frueher `rechte`)
   * ist aus `rechte` abgeleitet (ein boolean, wechselt nur mit dem Recht); keine davon aendert sich beim Oeffnen eines
   * Spaltenfilters.
   */
  const zelleAngeklickt = useCallback(
    (id: string, key: string): void => {
      const bestellung = bestellungen.find((b) => b.id === id);
      if (!bestellung) return;
      // AUFTRAG "Klick auf nicht aenderbare Grid-Zelle oeffnet die Bestellung" (2026-10-04): was man im
      // Grid nicht aendern kann, oeffnet die Bestellung - im Drawer an der Stelle, an der das Feld
      // steht (`lib/zellZiel.ts`) - statt eine Meldung zu zeigen. "Nicht aenderbar" ist:
      //
      // - OHNE `edit_orders` JEDE Zelle. Das Recht entscheidet VOR der Spaltenpruefung (wie bisher):
      //   ein Uebernahmeversuch ginge gegen einen Endpunkt, der ihn mit 403 abweist (BEWUSSTE
      //   ABWEICHUNG VOM BESTAND, 2026-08-15, gleiche Bauart wie `spaltenEditorGesperrt`). Der Drawer
      //   graut seine Bedienelemente ohne das Recht selbst aus (`darfTabBearbeiten`) - er zeigt also
      //   dasselbe, nur lesend; `NUR_LESEN_TITEL` als Meldung entfaellt.
      // - eine Spalte aus `NICHT_BEARBEITBAR` oder eine Zeilensperre aus `orderListEditorRegel`
      //   (Status eines Sammelsendungs-Mitglieds). Der Grund steht weiter als `title` an der Zelle
      //   (`Bestellzeile.tsx`), die Meldung entfaellt.
      //
      // Keine Quelle fuer die verknuepfte Sendung in der Listenansicht (`null`, siehe Kommentar an
      // `orderListEditorRegel`): der DSV-Zweig feuert hier nie.
      if (!darfBearbeiten || orderListEditorRegel(key, null, bestellung).art === "gesperrt") {
        zeileOeffnen(id, zellZiel(key));
        return;
      }
      setzeBearbeitung({ id, key });
    },
    [bestellungen, darfBearbeiten, zeileOeffnen],
  );

  /**
   * `commitOrderListEdit` (:7628) und `cancelOrderListEdit` (:7622) - beide schliessen den Editor,
   * nur einer gibt den Wert weiter. Die Pruefung "steht diese Zelle ueberhaupt in Bearbeitung"
   * stammt woertlich von dort: ein spaeter eintreffendes `blur` einer bereits geschlossenen Zelle
   * darf weder schreiben noch eine INZWISCHEN geoeffnete andere Zelle zuklappen.
   */
  /**
   * Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung beheben" (2026-09-06):
   * `useCallback` - unten direkt als `onUebernehmen`/`onAbbrechen` an `Bestellzeile` durchgereicht
   * (ERSETZT zwei vorher bei JEDEM Render neu erzeugte Inline-Closures, siehe dortiger Aufruf). Die
   * Signatur passt fuer BEIDE Props unveraendert: `onUebernehmen` verlangt `wert: string`,
   * `onAbbrechen` nur `id`/`key` - ein optionales drittes Argument erfuellt beide Vertraege.
   */
  const bearbeitungBeenden = useCallback(
    (id: string, key: string, wert?: string): void => {
      if (bearbeitung?.id !== id || bearbeitung?.key !== key) return;
      setzeBearbeitung(null);
      if (wert !== undefined) onZelleAendern?.(id, key, wert);
    },
    [bearbeitung, onZelleAendern],
  );

  const filtern = (key: string, wert: Spaltenfilterwert | undefined) => {
    const naechster = { ...spaltenfilter };
    // index.html:7390: ein Wert, der NICHT einschraenkt (fehlt, leeres Array, leerer Bereich - siehe
    // `orderListSpaltenfilterAktiv`), LOESCHT den Eintrag statt ihn leer abzulegen.
    if (wert !== undefined && orderListSpaltenfilterAktiv(key, wert)) naechster[key] = wert;
    else delete naechster[key];
    setzeSpaltenfilter(naechster);
  };

  const bestellzeile = (order: Order, eingerueckt?: boolean) => (
    <Bestellzeile
      key={order.id as string}
      bestellung={order}
      spalten={columns}
      definitionen={definitionen}
      lieferanten={lieferanten}
      onOeffnen={onOeffnen}
      ansicht={ansicht}
      onKontextmenue={onKontextmenue}
      onBearbeiten={zelleAngeklickt}
      bearbeitet={bearbeitung && bearbeitung.id === order.id ? bearbeitung.key : null}
      onUebernehmen={bearbeitungBeenden}
      onAbbrechen={bearbeitungBeenden}
      umzugRef={umzugRef.current}
      onZeileAngeklickt={zeileAngeklickt}
      ausgewaehlt={ausgewaehlt.has(order.id as string)}
      eingerueckt={eingerueckt}
      nurLeser={nurLeser}
    />
  );

  /** Task 6: die Elternzeile samt ihren (eingerueckten) Kindern; Anzeigetexte werden hier aufgeloest. */
  const sammelzeile = (gruppe: Sammelsendungsgruppe) => {
    const gemeinsam = gruppe.parent.sharedValues;
    const erstes = gruppe.mitglieder[0];
    const gemischt = gemeinsam.mixedFields.includes("boardStatus");
    const statusLabel = gemischt
      ? "Status gemischt"
      : gemeinsam.boardStatus
        ? (definitionen.STATUS_MAP[gemeinsam.boardStatus]?.label ?? gemeinsam.boardStatus)
        : "Ohne Status";
    const lieferantName = orderSupplier(erstes, lieferanten)?.name ?? erstes.supplier;
    // Weichen die Kinder im Lieferanten ab (`mixedFields`), steht "gemischt" statt des Lieferanten des
    // ersten Kindes (Spec: "Lieferant (bei Abweichung gemischt)").
    const lieferant = gemeinsam.mixedFields.includes("supplierId")
      ? "Lieferant gemischt"
      : typeof lieferantName === "string" && lieferantName
        ? lieferantName
        : null;
    // Ein Override gilt nur bei EINER Waehrung; bei mehreren Waehrungen stehen die Summen je Waehrung
    // nebeneinander und nichts wird addiert. Ohne Kinder-Summen (Sonderfall) faellt die Anzeige leer aus.
    const summen = gruppe.parent.calculatedPaymentTotals;
    const overrideGilt =
      gemeinsam.paymentTotalOverride != null && summen.length <= 1 && summen[0]?.currency !== WAEHRUNG_UNBEKANNT;
    const gesamtbetrag = overrideGilt
      ? fmtBetragInWaehrung(gemeinsam.paymentTotalOverride as number, summen[0]?.currency ?? "USD")
      : fmtSummeJeWaehrung(summen);
    // Im Kopf trennt "·" die Teile; mehrere Waehrungen stehen dort mit " / " getrennt.
    const gesamtbetragKopf = overrideGilt ? gesamtbetrag : fmtSummeJeWaehrung(summen, " / ");
    // Je sichtbarer Spalte der aggregierte Wert der Kinder DIESES Bereichs (`gruppe.mitglieder`).
    const spaltenSchluessel = columns.map((spalte) => spalte.key);
    const { zellen, kopf } = baueAggregatZellen({
      schluessel: spaltenSchluessel,
      kinder: gruppe.mitglieder,
      dsvShipmentNo: gruppe.parent.dsvShipmentNo,
      definitionen,
      zentral: {
        lieferant,
        statusLabel,
        statusId: gemischt ? null : gemeinsam.boardStatus || null,
        gesamtbetrag,
        gesamtbetragKopf,
        gesamtbetragManuell: overrideGilt,
        faelligkeit: gemeinsam.listDueDate ?? null,
      },
    });
    // Die Faelligkeitszelle ist die EINZIGE bearbeitbare Aggregatzelle (Plan 2026-10-31, Task 6): sie bekommt die Huelle
    // um die fertig gezeichnete Anzeige. Recht wie bei den Zellen der Kinderzeile (`zelleAngeklickt`).
    const zellenMitEditor =
      zellen.due === undefined
        ? zellen
        : {
            ...zellen,
            due: (
              <SammelFaelligkeitZelle
                inhalt={zellen.due}
                zentral={gemeinsam.listDueDate ?? null}
                dsvShipmentNo={gruppe.parent.dsvShipmentNo}
                darfBearbeiten={darfBearbeiten}
                onSetzen={(wert) => onSammelFaelligkeitSetzen?.(gruppe.shipmentId, gemeinsam.rowVersion, wert)}
              />
            ),
          };
    return (
      <Sammelsendungszeile
        key={sammelsendungsSchluessel(gruppe)}
        shipmentId={gruppe.shipmentId}
        dsvShipmentNo={gruppe.parent.dsvShipmentNo}
        memberCount={gruppe.parent.memberCount}
        sichtbareAnzahl={gruppe.mitglieder.length}
        offen={aufgeklappt.has(sammelsendungsSchluessel(gruppe))}
        onUmschalten={() =>
          setzeAufgeklappt((vorher) => {
            const naechste = new Set(vorher);
            const schluessel = sammelsendungsSchluessel(gruppe);
            if (!naechste.delete(schluessel)) naechste.add(schluessel);
            return naechste;
          })
        }
        spalten={spaltenSchluessel}
        zellen={zellenMitEditor}
        kopfWerte={kopf}
        onSammelsendungOeffnen={onSammelsendungOeffnen}
      >
        {gruppe.mitglieder.map((mitglied) => bestellzeile(mitglied, true))}
      </Sammelsendungszeile>
    );
  };

  return (
    <div id={id} className="grouped-order-list">
      <div className="order-list-shell">
        <Seitenkopf
          titel="Bestellboard"
          unterzeile={zaehlerText(orders.length, facetOrders.length, activeColumnFilters.length + (neu ? 1 : 0))}
        />
        <Werkzeugleiste
          aktiveSpaltenfilter={activeColumnFilters.length}
          suche={sucheRoh}
          states={states}
          onSuche={setzeSucheRoh}
          onStates={setzeStates}
          onFilterZuruecksetzen={() => setzeSpaltenfilter({})}
          neu={neu}
          neuAnzahl={neuAnzahl}
          onNeu={setzeNeu}
          onSpaltenEditor={onSpaltenEditor}
          onBreitenZuruecksetzen={onBreitenZuruecksetzen}
          spaltenEditorGesperrt={spaltenEditorGesperrt}
          ausgewaehlteAnzahl={ausgewaehlt.size}
          onSammelStatus={() => onSammelStatusAnfordern?.(Array.from(ausgewaehlt))}
          bestellgruppeGesperrt={bestellgruppeGesperrt}
          onBestellgruppeAnlegen={() => setBestellgruppeDialogOffen(true)}
          nurLeser={nurLeser}
        />
        <div className="order-list-scroll">
          <table className="order-list" style={{ width: `${orderListTableWidth(columns, breiten)}px` }}>
            <colgroup>
              {columns.map((column) => (
                <col
                  key={column.key}
                  data-order-list-column={column.key}
                  style={{ width: `${orderListColumnWidth(column.key, breiten)}px` }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column) => (
                  <Spaltenkopf
                    key={column.key}
                    label={column.label}
                    schluessel={column.key}
                    facetOrders={facetOrders}
                    definitionen={definitionen}
                    lieferanten={lieferanten}
                    breiten={breiten}
                    sortierung={sortierung}
                    filterwert={spaltenfilter[column.key]}
                    onSortieren={sortieren}
                    onFiltern={filtern}
                    onFilterLeeren={(key) => filtern(key, undefined)}
                    offen={geoeffneterSpaltenfilter === column.key}
                    onOffenAendern={offenAendernHandler(column.key)}
                    onBreiteZiehen={onBreiteZiehen}
                    onBreiteTaste={onBreiteTaste}
                  />
                ))}
              </tr>
            </thead>
            {BOARD_GROUPS.map((group) => {
              const groupOrders = gruppierung.einzeln.filter(
                (order) => boardGroupId(order, definitionen, bestellungen) === group.id,
              );
              const groupSammel = gruppierung.jeBereich.get(group.id) ?? [];
              const collapsed = !!eingeklappt[group.id];
              return (
                <tbody
                  key={group.id}
                  data-board-group={group.id}
                  onDragOver={gruppeUeberfahren}
                  onDragLeave={gruppeVerlassen}
                  onDrop={(ereignis) => {
                    // Kontotyp "Nur lesen": nichts ablegen (die Zeilen sind ohnehin nicht ziehbar).
                    if (nurLeser) return;
                    const ergebnis = zeileAbgelegt(ereignis, group.id, umzugRef.current);
                    if (!ergebnis) return;
                    // Befund 5, Option C (2026-08-11): die gezogene id lebt in `umzugRef` ueber die
                    // gesamte Dauer des Ziehens - anders als die direkt iterierten Zeilen der Tabelle
                    // kann sie in dieser Zeit "baumeln", wenn die Bestellung waehrenddessen aus
                    // `bestellungen` verschwindet (Archivierung/Loeschung in einer anderen Sitzung).
                    // Erst NACH diesem Fund duerfen `onAendern`/die Meldung ueberhaupt lesen.
                    const o = bestellungen.find((b) => b.id === ergebnis.id);
                    if (!o) return; // kein Rechnen mit nicht geladener Bestellung
                    if (orderListZustand(o) !== "active") return;
                    if (ausgewaehlt.has(ergebnis.id) && ausgewaehlt.size > 1) {
                      // Sammel-Bereichswechsel: die GESAMTE aktuelle Auswahl wandert mit, nicht nur
                      // die gezogene Zeile. KEIN `onMeldung?.(...)` hier - anders als beim
                      // Einzelzug (dessen Meldung SOFORT, optimistisch, vor jedem Netzaufruf
                      // erscheint) ist der Ausgang eines Sammel-Schreibvorgangs (Erfolg/
                      // Teilkonflikt) nicht im Voraus bekannt. Die eigentliche Meldung baut - wie
                      // bei der Sammel-Statusaenderung - der Aufrufer in `App.tsx` NACH dem
                      // tatsaechlichen Ergebnis.
                      onSammelAendern?.(Array.from(ausgewaehlt), ergebnis.patch);
                    } else {
                      onAendern?.(ergebnis.id, ergebnis.patch);
                      // index.html:6634 - `moveOrderToBoardGroup`s Meldung, mit demselben Rueckfall
                      // wie dort (`orderNo || name || 'Bestellung'`).
                      // offen: Paket 3 – Scheinfunktion (Inventar E): die Meldung erscheint SOFORT, vor dem PATCH; scheitert er, steht sie trotzdem da, und nur der Fehlerstapel sagt, dass der Zug nicht gespeichert wurde
                      onMeldung?.(
                        `„${(o.orderNo as string) || (o.name as string) || "Bestellung"}“ → ${group.label}`,
                      );
                    }
                  }}
                >
                  <Gruppenzeile
                    gruppe={group}
                    bestellungen={[...groupOrders, ...groupSammel.flatMap((gruppe) => gruppe.mitglieder)]}
                    lieferanten={lieferanten}
                    eingeklappt={collapsed}
                    spaltenzahl={columns.length}
                    onUmschalten={(id) => onEingeklappt?.(id)}
                  />
                  {collapsed
                    ? null
                    : groupOrders.length || groupSammel.length
                      ? (gruppierung.abfolgeJeBereich.get(group.id) ?? []).map((zeile) =>
                          zeile.art === "einzeln" ? bestellzeile(zeile.bestellung) : sammelzeile(zeile.gruppe),
                        )
                      : (
                          <tr className="order-list-group-empty">
                            <td colSpan={columns.length}>Keine Bestellungen in diesem Abschnitt.</td>
                          </tr>
                        )}
                </tbody>
              );
            })}
          </table>
        </div>
      </div>
      <BestellgruppeAnlegenDialog
        offen={bestellgruppeDialogOffen}
        bestellungen={gruppenAuswahl}
        onBestellgruppeAnlegen={onBestellgruppeAnlegen ?? (() => {})}
        onAbbrechen={() => setBestellgruppeDialogOffen(false)}
      />
    </div>
  );
}
